import { cn } from "@drpcrd/utils/shared";
import { Badge, type BadgeProps } from "../badge";
import { CampaignAvatar } from "../campaign-avatar";
import { CreatorAvatar } from "../creator/creator-avatar";
import { Typography } from "../typography";
import {
  CreatorStack,
  type CreatorStackProps,
} from "../creator-stack";

export type DealCardProps = {
  name: string;
  subtitle?: string;
  campaignImageSrc?: string | null;
  creatorStackCreators?: CreatorStackProps["creators"];
  note?: string;
  badgeLabel?: string;
  badgeVariant?: BadgeProps["variant"];
  badgeIcon?: BadgeProps["icon"];
  avatarSrc?: string | null;
  social1Src?: string | null;
  social2Src?: string | null;
  onClick?: () => void;
  className?: string;
};

type BadgeVariant = Exclude<BadgeProps["variant"], null | undefined>;

const badgeIconColorByVariant: Partial<Record<BadgeVariant, string>> = {
  primary: "text-primary-500",
  lightGreen: "text-green-500",
  secondary: "text-pink-500",
};

/**
 * Presentational creator/deal card — avatar, name, optional note and status
 * badge. Extracted from the brand app's deal board so it can be reused
 * across dashboards that show creators moving through a deal pipeline.
 */
export const DealCard = ({
  name,
  subtitle,
  campaignImageSrc,
  creatorStackCreators,
  note,
  badgeLabel,
  badgeVariant = "light",
  badgeIcon,
  avatarSrc,
  social1Src,
  social2Src,
  onClick,
  className,
}: DealCardProps) => {
  const isDeclined =
    badgeLabel === "Declined by creator" || badgeLabel === "Declined by brand";
  const resolvedBadgeVariant: BadgeVariant = badgeVariant ?? "light";

  return (
    <div
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      className={cn(
        "hover:shadow-light w-full rounded-lg border border-neutral-500 p-2 transition-shadow",
        isDeclined ? "bg-neutral-150" : "bg-white",
        onClick && "cursor-pointer hover:border-neutral-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500",
        className,
      )}
    >
      <div className="flex min-w-0 flex-1 flex-col items-start gap-1">
        <div className="flex w-full items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <Typography
              size="sm"
              weight="semibold"
              color={isDeclined ? "neutral" : "black"}
              className="truncate leading-2.75 tracking-normal"
            >
              {name}
            </Typography>
            {subtitle && (
              <Typography
                size="sm"
                color={isDeclined ? "neutral" : "black"}
                className="truncate"
              >
                {subtitle}
              </Typography>
            )}
            {note && (
              <Typography size="sm" color="neutral" className="truncate">
                {note}
              </Typography>
            )}
            {isDeclined && (
              <Typography size="sm" weight="normal" color="neutral" className="leading-2.75 tracking-normal">
                {badgeLabel}
              </Typography>
            )}
          </div>

          <div className="flex shrink-0 items-start">
            <div className="z-0 -mr-0.5 h-4 w-4 shrink-0 overflow-hidden rounded-full border-2 border-white">
              <CampaignAvatar logo={campaignImageSrc ?? null} className="h-full w-full border-0" />
            </div>

            <div className="z-10 h-4 w-4 overflow-hidden rounded-full border-2 border-white">
              {creatorStackCreators && creatorStackCreators.length > 0 ? (
                <CreatorStack
                  creators={creatorStackCreators}
                  avatarSize="sm"
                />
              ) : (
                <CreatorAvatar
                  creatorSrc={avatarSrc}
                  social1Src={social1Src}
                  social2Src={social2Src}
                  name={name}
                  className="h-full w-full"
                />
              )}
            </div>
          </div>
        </div>

        {badgeLabel && !isDeclined && (
          <Badge
            icon={badgeIcon}
            iconClassName={badgeIconColorByVariant[resolvedBadgeVariant]}
            label={badgeLabel}
            variant={resolvedBadgeVariant}
            className={cn(
              "text-sm font-bold",
              badgeIcon ? "gap-1 py-0.5 pl-1.5 pr-1" : "px-1.5 py-0.5",
            )}
          />
        )}
      </div>
    </div>
  );
};
